import { CompanyLineItemVisibility } from '@breezy/shared'
import { ConfigProvider, Tag } from 'antd'
import React from 'react'
import { PricebookPhotoThumbnail } from '../../components/PricebookPhotoThumbnail/PricebookPhotoThumbnail'
import { HtmlRenderer } from '../../elements/HtmlRenderer/HtmlRenderer'
import { DEFAULT_ESTIMATE_OPTION_NAME, Option } from './estimatesFlowUtils'
import { OptionContainer } from './OptionContainer'

export type BasicEstimateOptionProps = {
  option: Option
  index: number
  className?: string
  style?: React.CSSProperties
  hideRecommendedStyling?: boolean
  footer?: React.ReactNode
  subtotalCollapsible?: boolean
  anchorFooter?: boolean
  showLoanDisclosure?: boolean
  showPromoPrequal?: boolean
  noBorder?: boolean
  hideFeaturedThumbnail?: boolean
  customerFacing?: boolean
  lineItemVisibilityStatus: CompanyLineItemVisibility
}

export const BasicEstimateOption = React.memo<BasicEstimateOptionProps>(
  ({
    option,
    index,
    className,
    style,
    hideRecommendedStyling,
    footer,
    subtotalCollapsible,
    anchorFooter,
    showLoanDisclosure,
    showPromoPrequal,
    noBorder,
    hideFeaturedThumbnail,
    customerFacing,
    lineItemVisibilityStatus,
  }) => {
    const { displayName, descriptionHtml, lineItems, discounts, recommended, featuredPhotoCdnUrl, taxConfiguration } =
      option

    const resolvedDisplayName = displayName || DEFAULT_ESTIMATE_OPTION_NAME

    const showRecommended = recommended && !hideRecommendedStyling

    return (
      <OptionContainer
        noBorder={noBorder}
        customerFacing={customerFacing}
        showLoanDisclosure={showLoanDisclosure}
        showPromoPrequal={showPromoPrequal}
        recommended={showRecommended}
        selected={option.selected}
        className={className}
        style={style}
        lineItems={lineItems}
        discounts={discounts}
        subtotalCollapsible={subtotalCollapsible}
        lineItemVisibilityStatus={lineItemVisibilityStatus}
        priceEngineOptions={{
          taxRoundingStrategy: taxConfiguration.roundingStrategy,
        }}
        header={
          <div className="flex flex-row">
            {!hideFeaturedThumbnail && (
              <div>
                <PricebookPhotoThumbnail large cdnUrl={featuredPhotoCdnUrl} />
              </div>
            )}
            <div className="flex flex-col justify-center">
              <div className="flex flex-row items-center text-xl font-semibold">
                <span className="mr-2">
                  #{index + 1}: {resolvedDisplayName}
                </span>
                {option.selected && (
                  <ConfigProvider
                    theme={{
                      token: {
                        colorBorder: '#389E0D',
                      },
                      components: {
                        Tag: {
                          defaultBg: '#F6FFED',
                          defaultColor: '#389E0D',
                        },
                      },
                    }}
                  >
                    <Tag className="font-normal">Accepted</Tag>
                  </ConfigProvider>
                )}
                {showRecommended && (
                  <ConfigProvider
                    theme={{
                      token: {
                        colorBorder: '#1677FF',
                      },
                      components: {
                        Tag: {
                          defaultBg: '#E6F4FF',
                          defaultColor: '#1677FF',
                        },
                      },
                    }}
                  >
                    <Tag className="font-normal">Recommended</Tag>
                  </ConfigProvider>
                )}
              </div>
              {descriptionHtml && (
                <div className="mt-2 text-sm">
                  <HtmlRenderer htmlContent={descriptionHtml} />
                </div>
              )}
            </div>
          </div>
        }
        footer={footer}
        anchorFooter={anchorFooter}
      />
    )
  },
)

BasicEstimateOption.displayName = 'BasicEstimateOption'
